import { RiCloseLargeFill, RiDeleteBin6Line } from "react-icons/ri";
import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { format, isValid } from "date-fns";
import axios from "axios";
import DynamicSelect from "../DynamicSelect";
import { PrintBillLayout } from "../PrintBillLayout";
import { useAuth } from "../../utils/AuthContext";

// API Constants
const API_CONFIG = {
  BASE_URL: "https://api.muktihospital.com",
  BILL_API: "/api/medicine-bill",
  MEDICINE_CATEGORY_API: "/api/pharmacy-category",
  MEDICINE_API: "/api/medicine",
  PATIENT_API: "/api/patient",
  PURCHASE_API: "/api/medicine-purchase",
  API_KEY: "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
  ADD_PATIENT_API_URL: "/api/patient/add",
};

// Axios instance with timeout
const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 5000,
  headers: { "X-Api-Key": API_CONFIG.API_KEY },
});

/**
 * Retries a failed API request with exponential backoff
 * @param {Object} config - Axios request config
 * @param {number} maxRetries - Maximum retry attempts
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise} - Resolved response or rejected error
 */
const retryRequest = async (config, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await axiosInstance(config);
    } catch (err) {
      if (i === maxRetries - 1) throw err;
      await new Promise((resolve) => setTimeout(resolve, delay * 2 ** i));
    }
  }
};

/**
 * Custom Bill Search Input Component
 */
const BillSearchInput = ({
  bills,
  selectedBillId,
  setSelectedBillId,
  isLoading,
  initialBillNumber,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialBillNumber || "");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (selectedBillId) {
      const selectedBill = bills.find((bill) => bill.value === selectedBillId);
      setSearchTerm(
        selectedBill ? selectedBill.label : initialBillNumber || ""
      );
    } else {
      setSearchTerm(initialBillNumber || "");
    }
  }, [selectedBillId, bills, initialBillNumber]);

  const filteredBills = useMemo(
    () =>
      bills.filter((bill) =>
        bill.label.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [bills, searchTerm]
  );

  const handleSelectBill = (bill) => {
    setSelectedBillId(bill.value);
    setSearchTerm(bill.label);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          const value = e.target.value;
          setSearchTerm(value);
          setIsOpen(value.length > 0 && filteredBills.length > 0);
          if (!value) setSelectedBillId(null);
        }}
        placeholder="Search Invoice Number"
        disabled={isLoading}
        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {isOpen && filteredBills.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
          {filteredBills.map((bill) => (
            <li
              key={bill.value}
              onClick={() => handleSelectBill(bill)}
              className="p-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 transition-colors duration-150"
            >
              {bill.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const GenerateBillModal = ({ isOpen, onClose, onSave, billId }) => {
  const { user } = useAuth();

  // State Management
  const [formData, setFormData] = useState([]);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: "", phoneNumber: "" });
  const [errors, setErrors] = useState({});
  const [selectedPatientId, setSelectedPatientId] =
    useState("Walking Customer");
  const [note, setNote] = useState("");
  const [discount, setDiscount] = useState("0");
  const [discountType, setDiscountType] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentAmount, setPaymentAmount] = useState("0");
  const [netAmount, setNetAmount] = useState("0");
  const [image, setImage] = useState(null);
  const [existingDocumentUrl, setExistingDocumentUrl] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [categories, setCategories] = useState([]);
  const [rawMedicines, setRawMedicines] = useState([]);
  const [purchaseRecords, setPurchaseRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [bills, setBills] = useState([]);
  const [selectedBillId, setSelectedBillId] = useState(billId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState("0");
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedMedicineIds, setSelectedMedicineIds] = useState([]);
  const [selectedBillNumber, setSelectedBillNumber] = useState("");
  const [loadingAction, setLoadingAction] = useState(null);
  const fileInputRef = useRef(null);

  const paymentModes = [
    { value: "", label: "Select Payment Mode" },
    { value: "cheque", label: "Cheque" },
    { value: "cash", label: "Cash" },
    { value: "card", label: "Card" },
    { value: "upi", label: "UPI" },
  ];

  const getBatchNos = useMemo(
    () => (medicineId) => {
      if (!medicineId) return [];
      const selectedMedicine = rawMedicines.find(
        (med) => med.id === medicineId
      );
      if (!selectedMedicine) return [];
      const batches = purchaseRecords.filter(
        (purchase) =>
          purchase.medicineName.toLowerCase() ===
          selectedMedicine.medicineName.toLowerCase()
      );
      return batches.map((batch) => {
        const taxValue =
          typeof batch.tax === "number"
            ? batch.tax.toString()
            : parseFloat(batch.tax) || "0";
        if (isNaN(parseFloat(taxValue))) {
          console.warn(
            `Invalid tax value for batch ${batch.batchNo}: ${batch.tax}`
          );
        }
        return {
          value: batch.batchNo,
          label: batch.batchNo,
          expiryDate: batch.expiryDate || "",
          availableQuantity: batch.availableQuantity?.toString() || "0",
          salePrice:
            typeof batch.price === "number" ? batch.price.toFixed(2) : "0.00",
          tax: taxValue,
          purchaseId: batch.id,
          itemIndex: batch.itemIndex,
        };
      });
    },
    [rawMedicines, purchaseRecords]
  );

  // Filtered medicines for display
  const filteredMedicines = useMemo(() => {
    const purchaseMedicineNames = new Set(
      purchaseRecords.map((record) => record.medicineName.toLowerCase())
    );

    let filtered = rawMedicines.filter((medicine) =>
      purchaseMedicineNames.has(medicine.medicineName.toLowerCase())
    );

    if (selectedCategory !== "all") {
      const category = categories.find((cat) => cat.value === selectedCategory);
      if (category) {
        filtered = filtered.filter(
          (medicine) =>
            medicine.medicineCategory.toLowerCase() ===
            category.label.toLowerCase()
        );
      }
    }

    if (medicineSearchTerm) {
      filtered = filtered.filter((medicine) =>
        medicine.medicineName
          .toLowerCase()
          .includes(medicineSearchTerm.toLowerCase())
      );
    }

    // console.log("Filtered Medicines:", filtered.length, filtered.map(m => m.medicineName));
    return filtered;
  }, [
    rawMedicines,
    medicineSearchTerm,
    selectedCategory,
    categories,
    purchaseRecords,
  ]);

  // Calculate total, VAT, and net amount
  const calculateTotalAmount = useMemo(() => {
    const total = Math.round(
      formData.reduce((sum, row) => {
        const qty = parseInt(row.quantity) || 0;
        const price = parseFloat(row.salePrice) || 0;
        const rowTax = parseFloat(row.tax) || 0;
        return sum + qty * price * (1 + rowTax / 100);
      }, 0)
    );
    const discountAmt = parseFloat(discount) || 0;
    const net = Math.round(total - discountAmt);
    const vat = parseFloat(
      formData.reduce((sum, row) => {
        const qty = parseInt(row.quantity) || 0;
        const price = parseFloat(row.salePrice) || 0;
        const rowTax = parseFloat(row.tax) || 0;
        return sum + qty * price * (rowTax / 100);
      }, 0)
    );
    return {
      total: total.toString(),
      net: net.toString(),
      vat: vat.toString(),
    };
  }, [formData, discount]);

  // Effects
  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(format(new Date(), "MM/dd/yyyy h:mm a"));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setTotalAmount(calculateTotalAmount.total);
    setNetAmount(calculateTotalAmount.net);
    if (!selectedBillId) {
      setPaymentAmount(calculateTotalAmount.net);
    }
  }, [calculateTotalAmount]);

  useEffect(() => {
    if (selectedBillId) {
      fetchBillData(selectedBillId);
    }
  }, [selectedBillId, categories, rawMedicines, purchaseRecords]);

  /**
   * Resets form to initial state
   */
  const resetForm = () => {
    setFormData([]);
    setSelectedPatientId("Walking Customer");
    setNote("");
    setDiscount("0");
    setDiscountType("");
    setPaymentMode("cash");
    setPaymentAmount("0");
    setNetAmount("0");
    setImage(null);
    setExistingDocumentUrl("");
    setSelectedBillId(null);
    setShowPrintPreview(false);
    setPrintData(null);
    setMedicineSearchTerm("");
    setSelectedCategory("all");
    setSelectedMedicineIds([]);
    setSelectedBillNumber("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * Fetches bill data by ID
   * @param {string} billId - Bill ID to fetch
   */
  const fetchBillData = async (billId) => {
    try {
      const { data } = await axiosInstance.get(
        `${API_CONFIG.BILL_API}/${billId}`
      );
      const bill = data.bill;
      setSelectedBillNumber(bill.billNumber || "");
      const items = Array.isArray(bill.items) ? bill.items : [];
      const newFormData = items.map((item) => {
        const medicine = rawMedicines.find(
          (med) =>
            med.medicineName.toLowerCase() === item.medicineName.toLowerCase()
        );
        const category = categories.find(
          (cat) =>
            cat.label.toLowerCase() ===
            (item.medicineCategory?.toLowerCase() || "")
        );
        const purchase = purchaseRecords.find(
          (p) =>
            p.batchNo === item.batchNo &&
            p.medicineName.toLowerCase() === item.medicineName.toLowerCase()
        );
        const salePrice =
          parseFloat(purchase?.price || item.salePrice)?.toString() || "0";
        const taxValue = parseFloat(item.tax)?.toString() || "0";
        if (isNaN(parseFloat(taxValue))) {
          console.warn(
            `Invalid tax value for item ${item.medicineName}: ${item.tax}`
          );
        }

        return {
          medicineCategory: category?.value || "",
          medicineName: item.medicineName || "",
          medicineId: medicine?.id || "",
          batchNo: item.batchNo || "",
          expiryDate:
            item.expiryDate && isValid(new Date(item.expiryDate))
              ? format(new Date(item.expiryDate), "yyyy-MM-dd")
              : "",
          quantity: item.quantity?.toString() || "0",
          availableQuantity: purchase?.availableQuantity?.toString() || "0",
          originalAvailableQuantity:
            purchase?.availableQuantity?.toString() || "0",
          salePrice: salePrice,
          tax: taxValue,
          totalAmount: Math.round(parseFloat(item.totalAmount) || 0).toString(),
          purchaseId: item.purchaseId?.toString() || "",
          itemIndex: purchase?.itemIndex?.toString() || "",
        };
      });
      setFormData(newFormData);
      setSelectedMedicineIds(
        items.map((item) => {
          const medicine = rawMedicines.find(
            (med) =>
              med.medicineName.toLowerCase() === item.medicineName.toLowerCase()
          );
          return medicine?.id || "";
        })
      );
      setSelectedPatientId(bill.patientId || "Walking Customer");
      setNote(bill.note || "");
      setDiscount(parseFloat(bill.discount)?.toString() || "0");
      setDiscountType(bill.discountType || "");
      setPaymentMode(bill.paymentMode || "cash");
      setPaymentAmount(
        Math.round(parseFloat(bill.paymentAmount) || 0).toString()
      );
      setNetAmount(Math.round(parseFloat(bill.netAmount) || 0).toString());
      setExistingDocumentUrl(bill.documentUrl || "");
      setSelectedBillId(billId);
    } catch (error) {
      console.error("Fetch bill error:", error);
      toast.error(
        `Failed to fetch bill: ${error.response?.data?.error || error.message}`
      );
    }
  };

  /**
   * Fetches initial data for the modal
   */
  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      // Fetch all medicines
      let allMedicines = [];
      let medicinePage = 1;
      const medicineLimit = 100000;
      let hasMoreMedicines = true;

      while (hasMoreMedicines) {
        const response = await axiosInstance.get(API_CONFIG.MEDICINE_API, {
          params: { page: medicinePage, limit: medicineLimit },
        });
        const medicines = extractArrayFromResponse(
          response.data,
          "Medicine API"
        );
        allMedicines = [...allMedicines, ...medicines];
        hasMoreMedicines = medicines.length === medicineLimit;
        medicinePage += 1;
      }

      // Fetch all purchase records
      let allPurchases = [];
      let purchasePage = 1;
      const purchaseLimit = 100000;
      let hasMorePurchases = true;

      while (hasMorePurchases) {
        const response = await axiosInstance.get(API_CONFIG.PURCHASE_API, {
          params: { page: purchasePage, limit: purchaseLimit },
        });
        const purchases = extractArrayFromResponse(
          response.data,
          "Purchase API"
        );
        allPurchases = [...allPurchases, ...purchases];
        hasMorePurchases = purchases.length === purchaseLimit;
        purchasePage += 1;
      }

      const [categoryResponse, patientResponse, billResponse] =
        await Promise.all([
          axiosInstance.get(API_CONFIG.MEDICINE_CATEGORY_API),
          axiosInstance.get(API_CONFIG.PATIENT_API),
          axiosInstance.get(`${API_CONFIG.BILL_API}/list`, {
            params: { page: 1, limit: 100000 },
          }),
        ]);

      const fetchedCategories = extractArrayFromResponse(
        categoryResponse.data,
        "Category API"
      ) || [{ value: "fallback_category", label: "Default Category" }];
      const patientData = extractArrayFromResponse(
        patientResponse.data,
        "Patient API"
      );
      const billData = extractArrayFromResponse(
        billResponse.data.bills || [],
        "Bill API"
      );

      setCategories([{ value: "all", label: "All" }, ...fetchedCategories]);
      setRawMedicines(allMedicines);
      setPurchaseRecords(allPurchases);
      setPatients([
        { value: "Walking Customer", label: "Walking Customer" },
        ...patientData,
      ]);
      setBills(billData);
    } catch (error) {
      console.error("Fetch data error:", error);
      toast.error(
        `Failed to load data: ${error.response?.data?.error || error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Extracts and validates array data from API response
   * @param {Object|Array} data - API response data
   * @param {string} apiName - Name of the API for logging
   * @returns {Array} - Processed and validated array
   */
  const extractArrayFromResponse = (data, apiName) => {
    let arrayData = [];
    if (Array.isArray(data)) {
      arrayData = data;
    } else if (data && typeof data === "object") {
      const keys = [
        "data",
        "categories",
        "medicines",
        "patients",
        "purchases",
        "bills",
        "items",
        "records",
      ];
      for (const key of keys) {
        if (Array.isArray(data[key])) {
          arrayData = data[key];
          break;
        }
      }
    }

    let isValid = false;
    if (apiName === "Category API") {
      isValid = arrayData.every((item) => item?.id && item.name);
      arrayData = arrayData.map((item) => ({
        value: item.id.toString(),
        label: item.name,
      }));
    } else if (apiName === "Medicine API") {
      isValid = arrayData.every((item) => item?.id && item.medicineName);
      arrayData = arrayData.map((item) => ({
        id: item.id.toString(),
        medicineName: item.medicineName,
        medicineCategory:
          item.categoryName?.toString() ||
          item.medicineCategory?.toString() ||
          "",
        medicineImage: item.medicinePhoto
          ? `${API_CONFIG.BASE_URL}${
              item.medicinePhoto.startsWith("/") ? "" : "/"
            }${item.medicinePhoto}`
          : "https://placehold.co/100x100",
      }));
    } else if (apiName === "Patient API") {
      isValid = arrayData.every((item) => item?.id && item.name);
      arrayData = arrayData.map((item) => ({
        value: item.id,
        label: `${item.name} (${item.phoneNumber || "No Phone"})`,
        nameOnly: item.name,
      }));
    } else if (apiName === "Purchase API") {
      arrayData = arrayData.flatMap((record) => {
        let items = [];
        try {
          if (typeof record.items === "string") {
            items = JSON.parse(record.items);
          } else if (Array.isArray(record.items)) {
            items = record.items;
          }
        } catch (e) {
          console.error("Failed to parse purchase items for ID:", record.id, e);
          items = [];
        }

        // 🟢 2. Check if parsed items exist
        if (!items || items.length === 0) {
          // console.warn(`No items found in purchase record ID ${record.id}`);
          return [];
        }

        // 🟢 3. Map over the parsed 'items' array
        return items.map((item, index) => {
          const taxValue =
            typeof item.tax === "number" ? item.tax : parseFloat(item.tax) || 0;

          return {
            id: record.id.toString(),
            itemIndex: index.toString(),
            batchNo: item.batchNo || "",
            medicineName: item.medicineName || "",
            boxPrice: item.boxPrice ?? 0,
            quantity: parseInt(item.quantity) || 0,
            availableQuantity: parseInt(
              item.availableQuantity ?? item.quantity ?? 0
            ),
            expiryDate: item.expiryDate || "",
            tax: taxValue.toString(),
            medicineId: item.medicineId?.toString() || "",
            medicineCategory: item.medicineCategory || "",
            price:
              typeof item.price === "number"
                ? item.price
                : parseFloat(item.price) || 0,
          };
        });
      });
      isValid = arrayData.every(
        (item) =>
          item?.id &&
          item.itemIndex !== undefined &&
          item.batchNo &&
          item.medicineName &&
          item.boxPrice !== undefined &&
          item.quantity !== undefined &&
          item.availableQuantity !== undefined
      );
      if (!isValid) {
        console.error("Invalid purchase data:", arrayData);
        toast.error("Purchase data is missing required fields");
      }
    } else if (apiName === "Bill API") {
      isValid = arrayData.every((item) => item?.id && item.billNumber);
      arrayData = arrayData.map((bill) => ({
        value: bill.id.toString(),
        label: bill.billNumber,
      }));
    }

    if (!isValid && apiName !== "Purchase API") {
      console.error(`Invalid data format from ${apiName}:`, arrayData);
      toast.error(`Invalid data format for ${apiName}`);
    }
    return isValid ? arrayData : [];
  };

  const calculateDueOrReturnAmount = () => {
    const payment = parseInt(paymentAmount) || 0;
    const net = parseInt(netAmount) || 0;
    return {
      dueAmount: payment < net ? Math.round(net - payment).toString() : "0",
      returnAmount: payment > net ? Math.round(payment - net).toString() : "0",
    };
  };

  const handleSelectChange = (index, name) => (option) => {
    const value = option ? option.value : "";
    setFormData((prev) => {
      const newFormData = [...prev];
      newFormData[index] = { ...newFormData[index], [name]: value };

      if (name === "batchNo") {
        const batch = getBatchNos(newFormData[index].medicineId).find(
          (b) => b.value === value
        );
        if (batch) {
          newFormData[index] = {
            ...newFormData[index],
            batchNo: value,
            expiryDate:
              batch.expiryDate && isValid(new Date(batch.expiryDate))
                ? format(new Date(batch.expiryDate), "yyyy-MM-dd")
                : "",
            availableQuantity: batch.availableQuantity,
            originalAvailableQuantity: batch.availableQuantity,
            salePrice: batch.salePrice,
            tax: batch.tax,
            purchaseId: batch.purchaseId,
            itemIndex: batch.itemIndex,
            totalAmount: "0",
          };
          const qty = parseInt(newFormData[index].quantity) || 0;
          const price = parseFloat(batch.salePrice) || 0;
          const rowTax = parseFloat(batch.tax) || 0;
          newFormData[index].totalAmount = Math.round(
            qty * price * (1 + rowTax / 100)
          ).toString();
        } else {
          newFormData[index] = {
            ...newFormData[index],
            batchNo: "",
            expiryDate: "",
            quantity: "",
            availableQuantity: "0",
            originalAvailableQuantity: "0",
            salePrice: "",
            tax: "0",
            purchaseId: "",
            itemIndex: "",
            totalAmount: "0",
          };
          toast.error("Selected batch not found");
        }
      }
      return newFormData;
    });
  };

  const handleInputChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newFormData = [...prev];
      newFormData[index] = { ...newFormData[index], [name]: value };

      if (name === "quantity") {
        const originalQty =
          parseFloat(newFormData[index].originalAvailableQuantity) || 0;
        const enteredQty = parseFloat(value) || 0;
        if (originalQty === 0) {
          newFormData[index].quantity = "0";
          toast.error("No stock available for this medicine");
          return newFormData;
        }
        newFormData[index].availableQuantity =
          enteredQty <= originalQty
            ? (originalQty - enteredQty).toString()
            : originalQty.toString();
        if (enteredQty > originalQty) {
          newFormData[index].quantity = originalQty.toString();
          toast.error(`Quantity cannot exceed available (${originalQty})`);
        }
      }

      const qty = parseInt(newFormData[index].quantity) || 0;
      const price = parseFloat(newFormData[index].salePrice) || 0;
      const rowTax = parseFloat(newFormData[index].tax) || 0;
      newFormData[index].totalAmount = Math.round(
        qty * price * (1 + rowTax / 100)
      ).toString();

      return newFormData;
    });
  };

  const handleNewPatientInputChange = (e) => {
    const { name, value } = e.target;
    setNewPatient((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddNewPatient = async () => {
    const newErrors = {};
    if (!newPatient.name) newErrors.name = "Name is required";
    if (!newPatient.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      await retryRequest({
        method: "post",
        url: API_CONFIG.ADD_PATIENT_API_URL,
        headers: { "x-api-key": API_CONFIG.API_KEY },
        data: newPatient,
      });
      const res = await retryRequest({
        method: "get",
        url: API_CONFIG.PATIENT_API,
        headers: { "x-api-key": API_CONFIG.API_KEY },
      });
      const patientList = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const patientOptions = [
        { value: "Walking Customer", label: "Walking Customer" },
        ...patientList.map((patient) => ({
          value: patient.name,
          label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
          nameOnly: patient.name || "N/A",
        })),
      ];
      setPatients(patientOptions);
      setSelectedPatientId(newPatient.name);
      setNewPatient({ name: "", phoneNumber: "" });
      setIsNewPatientModalOpen(false);
      toast.success("New patient added successfully!");
    } catch (err) {
      console.error("Error adding new patient:", err);
      toast.error(
        `Failed to add new patient: ${err.response?.data?.error || err.message}`
      );
    }
  };

  const handleOtherInputChange = (setter) => (e) => {
    const value =
      e.target.name === "paymentAmount"
        ? Math.round(parseFloat(e.target.value) || 0).toString()
        : e.target.value;
    setter(value);
  };

  const handleImageChange = (setter) => (e) => {
    const file = e.target?.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024)
        return toast.error("Image size must be less than 5MB");
      if (!["image/jpeg", "image/png"].includes(file.type))
        return toast.error("Only JPEG or PNG images are allowed");
      setter(file);
    } else {
      setter(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveRow = (index) => {
    setFormData((prev) => {
      const newFormData = prev.filter((_, i) => i !== index);
      setSelectedMedicineIds((prev) => prev.filter((_, i) => i !== index));
      return newFormData;
    });
  };

  const handleMedicineClick = (medicineId) => {
    const medicine = rawMedicines.find((med) => med.id === medicineId);
    if (!medicine) return;

    const validBatches = getBatchNos(medicineId);
    if (validBatches.length === 0) {
      toast.error(`No available stock for ${medicine.medicineName}`);
      return;
    }

    const category = categories.find(
      (cat) => cat.label === medicine.medicineCategory
    );

    // Select the latest batch (furthest expiry date)
    const sortedBatches = validBatches.sort((a, b) => {
      const dateA = new Date(a.expiryDate);
      const dateB = new Date(b.expiryDate);
      return isValid(dateA) && isValid(dateB) ? dateB - dateA : 0;
    });
    const latestBatch = sortedBatches[0];

    const newRow = {
      medicineCategory: category?.value || "",
      medicineName: medicine.medicineName,
      medicineId: medicineId,
      batchNo: latestBatch ? latestBatch.value : "",
      expiryDate:
        latestBatch &&
        latestBatch.expiryDate &&
        isValid(new Date(latestBatch.expiryDate))
          ? format(new Date(latestBatch.expiryDate), "yyyy-MM-dd")
          : "",
      quantity: "",
      availableQuantity: latestBatch ? latestBatch.availableQuantity : "0",
      originalAvailableQuantity: latestBatch
        ? latestBatch.availableQuantity
        : "0",
      salePrice: latestBatch ? latestBatch.salePrice : "",
      tax: latestBatch ? latestBatch.tax : "0",
      totalAmount: "0.00",
      purchaseId: latestBatch ? latestBatch.purchaseId : "",
      itemIndex: latestBatch ? latestBatch.itemIndex : "",
    };

    setFormData((prev) => [...prev, newRow]);
    setSelectedMedicineIds((prev) => [...prev, medicineId]);
  };

  /**
   * Validates form data before submission
   * @returns {boolean} - True if valid, false otherwise
   */
  const validateForm = () => {
    if (formData.length === 0) {
      toast.error("Please add at least one medicine to the bill");
      return false;
    }

    for (const row of formData) {
      if (!row.batchNo)
        return toast.error("Please select a batch number"), false;
      if (!row.expiryDate || !isValid(new Date(row.expiryDate)))
        return toast.error("Invalid expiry date"), false;
      if (!row.quantity || parseFloat(row.quantity) <= 0)
        return toast.error("Quantity must be greater than 0"), false;
      if (parseFloat(row.quantity) > parseFloat(row.originalAvailableQuantity))
        return toast.error("Quantity cannot exceed available quantity"), false;
      if (parseFloat(row.originalAvailableQuantity) <= 0)
        return toast.error(`No stock available for ${row.medicineName}`), false;
      if (!row.salePrice || parseFloat(row.salePrice) <= 0)
        return toast.error("Invalid sale price"), false;
      if (isNaN(parseFloat(row.tax))) {
        console.error(`Invalid tax value for ${row.medicineName}: ${row.tax}`);
        return toast.error("Invalid tax value"), false;
      }
      if (!row.purchaseId) return toast.error("Invalid purchase record"), false;
      if (!row.itemIndex && row.itemIndex !== "0")
        return toast.error("Invalid item index"), false;
      const purchase = purchaseRecords.find(
        (p) => p.id === row.purchaseId && p.itemIndex === row.itemIndex
      );
      if (!purchase)
        return (
          toast.error(
            `Purchase record not found for ${row.medicineName}, batch ${row.batchNo}`
          ),
          false
        );
      if (purchase.availableQuantity < parseFloat(row.quantity))
        return (
          toast.error(
            `Insufficient quantity for ${row.medicineName}, batch ${row.batchNo}`
          ),
          false
        );
      if (purchase.quantity <= 0 || purchase.price <= 0)
        return (
          toast.error(
            `Invalid purchase data for ${row.medicineName}, batch ${row.batchNo}`
          ),
          false
        );
    }
    if (parseFloat(discount) < 0)
      return toast.error("Invalid discount value"), false;
    if (parseFloat(paymentAmount) < 0)
      return toast.error("Invalid payment amount"), false;
    if (parseFloat(totalAmount) <= 0)
      return toast.error("Total amount is invalid"), false;
    if (parseFloat(netAmount) <= 0)
      return toast.error("Net amount is invalid"), false;
    return true;
  };

  /**
   * Updates batch quantities in the purchase records
   * @param {Array} medicines - Array of medicine items
   * @param {boolean} isEdit - Indicates if editing an existing bill
   * @param {Array} oldMedicines - Previous medicines for edit mode
   */
  const updateBatchQuantities = async (
    medicines,
    isEdit = false,
    oldMedicines = []
  ) => {
    try {
      for (const med of medicines) {
        const purchase = purchaseRecords.find(
          (p) =>
            p.medicineName.toLowerCase() === med.medicineName.toLowerCase() &&
            p.batchNo === med.batchNo
        );
        if (!purchase)
          throw new Error(
            `Purchase record not found for ${med.medicineName} (batch ${med.batchNo})`
          );

        let newAvailableQuantity =
          parseInt(purchase.availableQuantity) - parseInt(med.quantity);
        if (isEdit) {
          const oldMed = oldMedicines.find(
            (old) =>
              old.medicineName.toLowerCase() ===
                med.medicineName.toLowerCase() && old.batchNo === med.batchNo
          );
          if (oldMed) {
            newAvailableQuantity += parseInt(oldMed.quantity);
          }
        }

        if (newAvailableQuantity < 0)
          throw new Error(
            `Cannot deduct ${med.quantity} from available quantity (${purchase.availableQuantity})`
          );
        if (newAvailableQuantity > parseInt(purchase.quantity))
          throw new Error(
            `Available quantity (${newAvailableQuantity}) cannot exceed original quantity (${purchase.quantity})`
          );

        // console.log('Patching availableQuantity:', {
        //   purchaseId: purchase.id,
        //   itemIndex: parseInt(purchase.itemIndex, 10),
        //   availableQuantity: newAvailableQuantity,
        // });

        const patchPayload = {
          itemIndex: parseInt(purchase.itemIndex, 10),
          availableQuantity: newAvailableQuantity,
        };
        const patchResponse = await retryRequest({
          method: "patch",
          url: `${API_CONFIG.PURCHASE_API}/${purchase.id}/available-quantity`,
          data: patchPayload,
          headers: {
            "x-api-key": API_CONFIG.API_KEY,
            "Content-Type": "application/json",
          },
        });

        // console.log('PATCH response:', patchResponse.data);

        setPurchaseRecords((prev) =>
          prev.map((p) =>
            p.id === purchase.id && p.itemIndex === purchase.itemIndex
              ? { ...p, availableQuantity: newAvailableQuantity }
              : p
          )
        );
      }
    } catch (err) {
      const errorMessage =
        err.code === "ERR_NETWORK"
          ? "Cannot reach server. Check if backend is running."
          : err.response
          ? `Server error: ${err.response.status} ${
              err.response.data?.error || err.response.statusText
            }`
          : err.message;
      console.error("Batch quantity update error:", err);
      console.error("Error response:", err.response?.data);
      toast.error(`Failed to update batch quantities: ${errorMessage}`);
      throw err;
    }
  };

  /**
   * Formats payload for bill submission
   * @returns {Object} - Formatted payload and medicines
   */
  const formatPayload = () => {
    const selectedPatientObject = patients.find(
      (p) => p.value === selectedPatientId
    );
    // Get the name from the object, or default to "Walking Customer"
    const customerName = selectedPatientObject
      ? selectedPatientObject.nameOnly || selectedPatientObject.label 
      : "Walking Customer";
    const taxAmount = parseFloat(
      formData.reduce((sum, row) => {
        const qty = parseInt(row.quantity) || 0;
        const price = parseFloat(row.salePrice) || 0;
        const rowTax = parseFloat(row.tax) || 0;
        return sum + qty * price * (rowTax / 100);
      }, 0)
    );

    const payload = {
      patientId:
        selectedPatientId === "Walking Customer" ? null : selectedPatientId,
      customerName: customerName,
      note: note || "",
      discount: parseFloat(discount) || 0,
      discountType: discountType || (parseFloat(discount) > 0 ? "flat" : ""),
      paymentMode: paymentMode || "",
      paymentAmount: parseInt(paymentAmount) || 0,
      totalAmount: parseInt(totalAmount),
      taxAmount: taxAmount,
      netAmount: parseInt(netAmount),
      dueAmount: parseInt(calculateDueOrReturnAmount().dueAmount),
      returnAmount: parseInt(calculateDueOrReturnAmount().returnAmount),
      userName: user?.name || user?.username || "Unknown",
      userPhone: user?.mobile || user?.mobile || "",
    };

    const medicines = formData.map((row) => {
      const category = categories.find(
        (cat) => cat.value === row.medicineCategory
      );
      return {
        medicineCategory: category?.label || "",
        medicineName: row.medicineName,
        batchNo: row.batchNo,
        expiryDate: row.expiryDate,
        quantity: parseInt(row.quantity),
        salePrice: parseFloat(row.salePrice) || 0,
        tax: parseFloat(row.tax) || 0,
        totalAmount: parseInt(row.totalAmount),
        purchaseId: row.purchaseId,
        itemIndex: row.itemIndex,
      };
    });

    return { payload, medicines };
  };

  /**
   * Submits the bill
   * @returns {Object} - Submission result
   */
  const handleSaveAndPrint = async () => {
    setLoadingAction('print');
    const result = await handleSubmit();

    if (!result.success) {
      setLoadingAction(null);
      return;
    }

    const { payload, medicines, billNumber } = result;
    const billInfo = (
      <div>
        <div className="space-y-1">
          <p>
            <strong>Bill No:</strong> {billNumber}
          </p>
          <p>
            <strong>Date:</strong> {currentDateTime}
          </p>
          <p>
            <strong>Customer Name:</strong> {payload.customerName || "None"}
          </p>
          {payload.userPhone && (
            <p>
              <strong>Phone:</strong> {payload.userPhone}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <p>
            <strong>Payment Mode:</strong>{" "}
            {payload.paymentMode
              ? payload.paymentMode.charAt(0).toUpperCase() +
                payload.paymentMode.slice(1)
              : "None"}
          </p>
          {existingDocumentUrl && (
            <p>
              <strong>Document:</strong>{" "}
              <a
                href={`${API_CONFIG.BASE_URL}${existingDocumentUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                View
              </a>
            </p>
          )}
        </div>
      </div>
    );

    const table = (
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 text-left">Sl No</th>
            <th className="p-2 text-left">Medicine</th>
            <th className="p-2 text-left">Category</th>
            <th className="p-2 text-left">Batch No</th>
            <th className="p-2 text-left">Expiry Date</th>
            <th className="p-2 text-right">Quantity</th>
            <th className="p-2 text-right">Price (৳)</th>
          </tr>
        </thead>
        <tbody>
          {medicines.map((med, index) => (
            <tr key={index} className="border-t">
              <td className="p-2">{index + 1}</td>
              <td className="p-2">{med.medicineName}</td>
              <td className="p-2">{med.medicineCategory}</td>
              <td className="p-2">{med.batchNo}</td>
              <td className="p-2">
                {format(new Date(med.expiryDate), "MM/dd/yyyy")}
              </td>
              <td className="p-2 text-right">{med.quantity}</td>
              <td className="p-2 text-right">{med.salePrice.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    const summary = (
      <div className="flex flex-col">
        <div className="text-gray-600">
          <span>Sub Total:</span>
          <span>৳{payload.totalAmount}</span>
        </div>
        <div className="text-gray-600">
          <span>Vat:</span>
          <span>৳{payload.taxAmount.toFixed(2)}</span>
        </div>
        <div className="text-gray-600">
          <span>Discount:</span>
          <span>৳{payload.discount.toFixed(2)}</span>
        </div>
        {payload.dueAmount > 0 && (
          <div className="text-gray-600">
            <span>Due Amount:</span>
            <span>৳{payload.dueAmount}</span>
          </div>
        )}
        {payload.returnAmount > 0 && (
          <div className="text-gray-600">
            <span>Return Amount:</span>
            <span>৳{payload.returnAmount}</span>
          </div>
        )}
        <div>
          <span>Net Amount:</span>
          <span>৳{payload.netAmount}</span>
        </div>
      </div>
    );

    const noteSection = payload.note ? (
      <div className="note-box">
        <strong>Note:</strong> {payload.note}
      </div>
    ) : null;

    const newPrintData = { billInfo, table, summary, note: noteSection };
    setPrintData(newPrintData);
    setShowPrintPreview(true);

    setTimeout(async () => {
      const elementsToHide = document.querySelectorAll(".print-hidden");
      elementsToHide.forEach((el) => (el.style.display = "none"));

      const printLayout = document.querySelector(".print-preview");
      if (printLayout) {
        printLayout.style.display = "block";
      } else {
        console.error("DEBUG: Print layout not found");
      }

      window.print();

      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (printLayout) {
          printLayout.style.display = "none";
        }
        setShowPrintPreview(false);
        setLoadingAction(null);
      }, 500);
    }, 1000);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return { success: false };
    setLoadingAction('save');
    try {
      const { payload, medicines } = formatPayload();
      const url = selectedBillId
        ? `${API_CONFIG.BILL_API}/${selectedBillId}`
        : `${API_CONFIG.BILL_API}/add`;
      const method = selectedBillId ? "put" : "post";

      let response;
      if (image) {
        const formDataPayload = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          formDataPayload.append(key, value);
        });
        medicines.forEach((med, index) => {
          Object.entries(med).forEach(([medKey, medValue]) => {
            formDataPayload.append(`medicines[${index}][${medKey}]`, medValue);
          });
        });
        formDataPayload.append("document", image);
        response = await axiosInstance({ method, url, data: formDataPayload, timeout: 30000 });
      } else {
        payload.medicines = medicines;
        response = await axiosInstance({ method, url, data: payload, timeout: 30000 });
      }

      const newBill = response.data.bill;
      if (!selectedBillId) {
        setBills((prev) => [
          ...prev,
          { value: newBill.id, label: newBill.billNumber },
        ]);
        resetForm();
        onSave("Bill created successfully", payload);
        await updateBatchQuantities(medicines);
        resetForm();
      } else {
        const oldBill = await axiosInstance.get(
          `${API_CONFIG.BILL_API}/${selectedBillId}`
        );
        await updateBatchQuantities(medicines, true, oldBill.data.bill.items);
        toast.success("Bill updated successfully!", { autoClose: 2000 });
      }
      return {
        success: true,
        payload,
        medicines,
        billNumber: newBill?.billNumber || "",
      };
    } catch (error) {
      console.error("API error response:", error.response?.data);
      const errorMessage =
        error.code === "ERR_NETWORK"
          ? "Cannot reach server. Check if backend is running."
          : error.response?.data?.error
          ? `Server error: ${error.response.status} ${error.response.data.error}`
          : error.message;
      toast.error(`Error saving bill: ${errorMessage}`);
      return { success: false };
    } finally {
      setLoadingAction(null);
    }
  };

  // Early return if modal is not open
  if (!isOpen) return null;

  const { dueAmount, returnAmount } = calculateDueOrReturnAmount();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full min-h-[calc(100vh-50px)] h-full overflow-y-auto pb-6 print-wrapper">
        {/* Modal Header */}
        <div className="flex flex-wrap gap-3 flex-row md:items-center justify-between bg-gray-200 border-b border-dashed border-M-text-color/50 py-4 px-6">
          <h2 className="text-lg font-medium text-black flex items-center gap-2">
            {selectedBillId ? "Edit Medicine Bill" : "Generate Medicine Bill"}
          </h2>
          <button
            onClick={onClose}
            title="Close"
            className="size-7 ml-auto rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
            disabled={isLoading}
          >
            <RiCloseLargeFill />
          </button>
        </div>

        {/* Modal Body */}
        <div className="my-4 px-6 h-[calc(100vh-250px)] lg:h-[calc(100vh-150px)] overflow-y-auto">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Input Area */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {/* Invoice No */}
              <div>
                <label
                  htmlFor="invoice-number"
                  className="label font-medium text-black"
                >
                  Search Invoice Number: <span className="text-red-600">*</span>
                </label>
                <BillSearchInput
                  bills={bills}
                  selectedBillId={selectedBillId}
                  setSelectedBillId={setSelectedBillId}
                  isLoading={isLoading}
                  initialBillNumber={selectedBillNumber}
                />
              </div>
              {/* Date */}
              <div>
                <label htmlFor="date" className="label font-medium text-black">
                  Date: <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={currentDateTime}
                  readOnly
                  className="input-field disabled:bg-gray-100"
                />
              </div>
              {/* Customer Name */}
              <div>
                <label
                  htmlFor="patient"
                  className="label font-medium text-black"
                >
                  Search Patient: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={patients}
                      value={
                        patients.find(
                          (option) => option.value === selectedPatientId
                        ) || null
                      }
                      onChange={(option) =>
                        setSelectedPatientId(
                          option?.value || "Walking Customer"
                        )
                      }
                      isSearchable
                      placeholder="Search Patient"
                      isDisabled={isLoading}
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setIsNewPatientModalOpen(true);
                    }}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    disabled={isLoading}
                  >
                    Add New
                  </button>
                </div>
              </div>
              {/* Document Upload */}
              <div>
                <label
                  htmlFor="invoice-number"
                  className="label font-medium text-black"
                >
                  Document:
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange(setImage)}
                  className="mt-1 block w-full text-sm text-gray-600 border border-gray-200 rounded-md p-[1px] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                  disabled={isLoading}
                  ref={fileInputRef}
                />
                {existingDocumentUrl && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Current Document:{" "}
                      <a
                        href={`${API_CONFIG.BASE_URL}${existingDocumentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                      >
                        View
                      </a>
                    </p>
                  </div>
                )}
              </div>
              {/* Payment Mode */}
              <div>
                <label
                  htmlFor="payment-mode"
                  className="label font-medium text-black"
                >
                  Payment Mode:
                </label>
                <DynamicSelect
                  options={paymentModes}
                  value={
                    paymentModes.find(
                      (option) => option.value === paymentMode
                    ) || null
                  }
                  onChange={(option) => setPaymentMode(option?.value || "")}
                  isSearchable
                  placeholder="Payment Mode"
                  controlBackgroundColor="transparent"
                  controlFontSize="13px"
                  isDisabled={isLoading}
                />
              </div>
            </div>

            {/* Table Content */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-6 mt-6 pt-6">
              <div>
                <div className="flex flex-col sm:flex-row gap-y-6 sm:gap-6">
                  {/* Search Medicine */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search Medicine
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring"
                      placeholder="Search medicines..."
                      value={medicineSearchTerm}
                      onChange={(e) => setMedicineSearchTerm(e.target.value)}
                    />
                  </div>
                  {/* Select Category Area */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Category
                    </label>
                    <DynamicSelect
                      options={categories}
                      value={
                        categories.find(
                          (cat) => cat.value === selectedCategory
                        ) || null
                      }
                      onChange={(option) =>
                        setSelectedCategory(option ? option.value : "")
                      }
                      isSearchable
                      placeholder="Select category"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {/* Medicine Image Area */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-3 2xl:grid-cols-5 gap-3 mt-4 py-2 max-h-[425px] overflow-y-auto scroll-smooth">
                  {filteredMedicines.map((medicine, index) => {
                    return (
                      <figure
                        key={medicine.id}
                        className={`border rounded-md p-2 shadow-sm cursor-pointer hover:border-blue-500 transition-all ${
                          selectedMedicineIds.includes(medicine.id)
                            ? "border-blue-500 border-2"
                            : ""
                        }`}
                        onClick={() => handleMedicineClick(medicine.id)}
                      >
                        <img
                          src={medicine.medicineImage}
                          alt={medicine.medicineName}
                          className="w-full h-24 object-cover rounded-md"
                          // 🟢 DEBUG LOG 3: Check if browser fails to load image
                          onError={(e) => {
                            console.error(
                              "❌ Image Load Failed for:",
                              medicine.medicineName
                            );
                            console.error("   Attempted URL:", e.target.src);

                            // Force visual feedback to verify element exists
                            e.target.style.border = "3px solid red";

                            // Fallback
                            e.target.src =
                              "https://placehold.co/100x100?text=No+Image";
                          }}
                          // 🟢 DEBUG LOG 4: Check if browser SUCCEEDS
                          onLoad={() =>
                            console.log(
                              "✅ Image Loaded:",
                              medicine.medicineName
                            )
                          }
                        />
                        <figcaption className="text-xs text-gray-700 text-center mt-1">
                          {medicine.medicineName}
                        </figcaption>
                      </figure>
                    );
                  })}
                  {/* )} */}
                </div>
              </div>
              {/* Table Area */}
              <div className="overflow-x-auto col-span-2">
                <table className="w-full table-fixed border-collapse min-w-[800px]">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Name <span className="text-red-600">*</span>
                      </th>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Batch No <span className="text-red-600">*</span>
                      </th>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Expiry Date <span className="text-red-600">*</span>
                      </th>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Quantity (Unit) | Availability{" "}
                        <span className="text-red-600">*</span>
                      </th>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Unit Price
                      </th>
                      <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                        Total
                      </th>
                      <th className="text-center text-xs text-black border border-gray-300 py-2 px-3 w-16 font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.map((row, index) => (
                      <tr key={index}>
                        {/* Medicine Name */}
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="text"
                            name="medicineName"
                            value={row.medicineName}
                            onChange={(e) => handleInputChange(index, e)}
                            placeholder="Enter medicine"
                            className="input-field disabled:bg-gray-100"
                            disabled
                          />
                        </td>
                        {/* Batch No */}
                        <td className="border border-gray-200 py-2 px-3">
                          <DynamicSelect
                            options={getBatchNos(row.medicineId)}
                            value={
                              getBatchNos(row.medicineId).find(
                                (batch) => batch.value === row.batchNo
                              ) || null
                            }
                            onChange={handleSelectChange(index, "batchNo")}
                            placeholder="Select Batch"
                            isSearchable
                            disabled={isLoading || !row.medicineName}
                          />
                        </td>
                        {/* Expiry Date */}
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="date"
                            name="expiryDate"
                            value={row.expiryDate}
                            readOnly
                            className="input-field disabled:bg-gray-100"
                            disabled
                          />
                        </td>
                        {/* Quantity | Available */}
                        <td className="border border-gray-200 py-2 px-3 relative">
                          <div className="relative rounded-md overflow-hidden">
                            <input
                              type="number"
                              name="quantity"
                              value={row.quantity}
                              onChange={(e) => handleInputChange(index, e)}
                              className="input-field"
                              placeholder="00"
                              min="0"
                              disabled={
                                isLoading ||
                                parseInt(row.availableQuantity, 10) === 0
                              }
                            />
                            <span className="text-xs text-M-text-color absolute top-0 right-0 h-full border-l border-gray-100 w-10 flex items-center justify-center">
                              {row.availableQuantity || "0"}
                            </span>
                          </div>
                        </td>
                        {/* Unit Sale Price */}
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            name="salePrice"
                            value={row.salePrice}
                            readOnly
                            className="input-field text-end disabled:bg-gray-100"
                            disabled
                            placeholder="00"
                          />
                        </td>
                        {/* Total */}
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            name="totalAmount"
                            value={row.totalAmount}
                            readOnly
                            className="input-field text-end disabled:bg-gray-100"
                            disabled
                            placeholder="00"
                          />
                        </td>
                        {/* Action */}
                        <td className="border border-gray-200 py-2 px-3">
                          <button
                            title="Remove"
                            className="size-7 mx-auto rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            onClick={() => handleRemoveRow(index)}
                            disabled={isLoading}
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {/* Calculation area */}

                    {/* Sub Total */}
                    <tr>
                      <td
                        className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                        colSpan={5}
                      >
                        Sub Total (৳)
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={totalAmount}
                          readOnly
                          className="input-field text-end disabled:bg-gray-100"
                          disabled
                          placeholder="0"
                        />
                      </td>
                    </tr>
                    {/* Vat */}
                    <tr>
                      <td
                        className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                        colSpan={5}
                      >
                        Vat (৳)
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={calculateTotalAmount.vat}
                          readOnly
                          className="input-field text-end disabled:bg-gray-100"
                          disabled
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                    {/* Discount */}
                    <tr>
                      <td
                        className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                        colSpan={5}
                      >
                        Discount (৳)
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          value={discount}
                          onChange={handleOtherInputChange(setDiscount)}
                          className={`input-field text-end ${
                            selectedBillId ? "bg-gray-100 cursor-not-allowed" : "" 
                          }`}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          disabled={isLoading}
                          readOnly={!!selectedBillId}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                {/* Note Area */}
                <div className="mt-4 pt-4">
                  <label htmlFor="note" className="label">
                    Note:
                  </label>
                  <textarea
                    rows="4"
                    value={note}
                    onChange={handleOtherInputChange(setNote)}
                    className="input-field"
                    placeholder="Note"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        {/* Modal Footer */}
        <div className="flex flex-col lg:flex-row justify-between items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <div className="flex items-center gap-6">
            <div className="flex flex-col xl:flex-row items-center gap-2">
              <p className="text-xs sm:text-sm font-semibold leading-none">
                Grand Total (TK):
              </p>
              <h4 className="text-3xl leading-none">৳{netAmount}</h4>
            </div>
            <div className="flex flex-col xl:flex-row items-center gap-2">
              <label
                htmlFor="painAmount"
                className="text-xs sm:text-sm font-semibold leading-none"
              >
                Paid Amount (TK):
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={handleOtherInputChange(setPaymentAmount)}
                className={`input-field text-end max-w-24 ${
                  selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="0"
                min="0"
                step="0"
                disabled={isLoading}
                readOnly={!!selectedBillId}
              />
            </div>
            <div className="flex flex-col xl:flex-row items-center gap-2">
              <p className="text-xs sm:text-sm font-semibold leading-none">
                Due/Return Amount (TK):
              </p>
              <h4 className="text-3xl leading-none">
                ৳{parseInt(dueAmount) > 0 ? dueAmount : returnAmount}
              </h4>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetForm}
              className="bg-gray-600 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndPrint}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {loadingAction === 'print' 
                  ? "Processing..." 
                  : (selectedBillId ? "Update & Print" : "Save & Print")
                }
            </button>
            <button
              onClick={handleSubmit}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {loadingAction === 'save' 
                  ? "Saving..." 
                  : (selectedBillId ? "Update" : "Save")
                }
            </button>
          </div>
        </div>

        {showPrintPreview && printData && (
          <div className="mt-6 print-preview">
            <PrintBillLayout
              className="print-preview"
              title="Bill Preview"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              note={printData.note}
            />
          </div>
        )}
        {isNewPatientModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md transform transition-all duration-300 scale-100">
              <h2 className="text-xl font-bold text-gray-800 mb-5">
                Add New Patient
              </h2>
              <div className="mb-4">
                <label className="label font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={newPatient.name}
                  onChange={handleNewPatientInputChange}
                  className={`input-field w-full p-2 border ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  } rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter patient name"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>
              <div className="mb-6">
                <label className="label font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={newPatient.phoneNumber}
                  onChange={handleNewPatientInputChange}
                  className={`input-field w-full p-2 border ${
                    errors.phoneNumber ? "border-red-500" : "border-gray-300"
                  } rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600`}
                  placeholder="Enter phone number"
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsNewPatientModalOpen(false)}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNewPatient}
                  className="bg-blue-600 text-sm text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-all duration-200"
                >
                  Add Patient
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateBillModal;
